import { InvestorDocumentWorkflowTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/InvestorDocumentWorkflowTable';
import { DashcardsContainerInvestorDetails as DashCards } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/Dashcards/DashcardsContainerInvestorDetails';
import { HoldingTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/HoldingTable/HoldingTable';
import { InvestmentTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/InvestmentTable/InvestmentTable';
import { BankDetailsSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/BankDetailsSection';
import { InvestorPersonalDetailsSection as InvestorPersonalInformationSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/InvestorPersonalDetailsSection';
import { PIVDetailsSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/PIVDetailsSection';
import { InvestorSection as ManageInvestorSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/InvestorSection';
import { TransactionTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionTable/TransactionTable';
import { useInvestmentReference } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/TransactionTable/useInvestmentReference.ts';
import { useManageInvestorDetailsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/useManageInvestorDetailsController';
import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { useManageInvestorsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/useManageInvestorsController';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import {
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	DATE_FORMATS,
	formatStringDateToRelative,
	formatStringDateToUI,
} from '@dealstack/shared/lib/format/dates/date';
import { isEmpty } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { LoanDefinitionType, SecurityType } from '@dealstack/shared/lib/generated';
import { Button, Flex, rem, Text, Tooltip } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useNavigateToMessages } from '@dealstack/frontend/app/pages/Project/MIP/Messages/hooks/useNavigateToMessages';
import { MIP_MANAGE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { mipManagePath } from '@dealstack/frontend/app/pages/Routing/routes.utils.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams';
import { PageAbsoluteContainer } from '@dealstack/frontend/shared/layout/PageAbsoluteContainer/PageAbsoluteContainer.tsx';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs';
import { AddBreadcrumbItem } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.types';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { useMipManageBreadcrumbsPrefix } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/utils/mipManageBreadcrumbs.utils';
import { PageHeader } from '@dealstack/frontend/shared/layout/PageHeader/PageHeader.tsx';
import { InvestorProgrammeStatusBadge } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/InvestorProgrammeStatusBadge.tsx';

export const ManageInvestorDetailsContext = React.createContext<
	ReturnType<typeof useManageInvestorDetailsController>
>({} as ReturnType<typeof useManageInvestorDetailsController>);

export const InvestorDetails = () => {
	const { dealId, mipId } = useRequiredParams('dealId', 'mipId');
	const manageInvestorDetailsController = useManageInvestorDetailsController();
	const navigate = useNavigate();
	const { navigateToMessages } = useNavigateToMessages(
		manageInvestorDetailsController.investorId
	);

	const manageInvestorsControllerV2 = useManageInvestorsController(false);

	const investorsListPath = mipManagePath(MIP_MANAGE_ROUTES.INVESTORS, { dealId, mipId });

	const navigateToTable = useCallback(() => {
		if ((window.history.state?.idx ?? 0) > 0) {
			navigate(-1);
		} else {
			navigate({ pathname: investorsListPath }, { replace: true });
		}
	}, [navigate, investorsListPath]);

	const tMipManage = useTranslationMipManage();
	const tDefault = useTranslationDefault();

	const mipManageController = useMIPManageContext();
	const programmeName = mipManageController?.mipData?.programmeName ?? '';
	const breadcrumbsPrefix = useMipManageBreadcrumbsPrefix({ dealId, mipId, programmeName });

	const breadcrumbs = useMemo<AddBreadcrumbItem[]>(
		() => [
			...breadcrumbsPrefix,
			{
				name: tMipManage('tabs.investors'),
				url: investorsListPath,
				hideInAppHeader: true,
				dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_BREADCRUMB,
				onClick: (ev) => {
					ev.preventDefault();
					navigateToTable();
				},
			},
			{
				name: tMipManage('investors.breadcrumbs.investor_details'),
				url: '',
				noLink: true,
				hideInAppHeader: true,
			},
		],
		[breadcrumbsPrefix, investorsListPath, navigateToTable, tMipManage]
	);
	useBreadcrumbs(breadcrumbs);

	const getInvestmentReference = useInvestmentReference();

	const getTypeTranslation = (type?: SecurityType | LoanDefinitionType): string => {
		if (!type) {
			return '';
		}

		const translationKey = type === SecurityType.OTHER ? `${type}_key` : type;
		return `${tDefault(`security_types.${translationKey.toLowerCase()}`)}`;
	};

	const checkIsTsaEmpty = useCallback(() => {
		if (manageInvestorDetailsController?.isFetchingInvestments) {
			return false;
		}
		return (
			isEmpty(manageInvestorDetailsController?.investments?.investmentDetails) &&
			isEmpty(manageInvestorDetailsController?.investments?.pendingInvestmentDetails)
		);
	}, [
		manageInvestorDetailsController?.getMIPInvestorDetailsDataRequest?.isFetching,
		manageInvestorDetailsController?.investments?.investmentDetails,
		manageInvestorDetailsController?.investments?.pendingInvestmentDetails,
	]);
	const investorPerson = manageInvestorDetailsController.investorDetailsData?.person;
	const lastLoginTime = manageInvestorDetailsController?.investorDetailsData?.lastLoginAt;

	const lastLoginItem = lastLoginTime && (
		<Tooltip label={formatStringDateToUI(lastLoginTime, DATE_FORMATS.CALENDAR_DATE)}>
			<Text fz="sm" c="dark.0">
				{tMipManage('investor_details.last_login', {
					lastLogin: formatStringDateToRelative(lastLoginTime),
					defaultValue: 'Last login: {{lastLogin}}',
				})}
			</Text>
		</Tooltip>
	);

	const headerContent = (
		<Flex direction="column">
			<Text
				size="B3Bold"
				c="dark.8"
				data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_NAME}
			>
				{investorPerson?.displayName}
			</Text>
			<Flex align="center" gap="0.5rem">
				<Text
					fz="lg"
					c="dark.0"
					data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_EMAIL}
				>
					{investorPerson?.email}
				</Text>

				<InvestorProgrammeStatusBadge
					programmeStatus={
						manageInvestorDetailsController.investorDetailsData?.programmeStatus
					}
				/>

				{lastLoginItem}
			</Flex>
		</Flex>
	);

	const childrenContent = (
		<ManageInvestorDetailsContext.Provider value={manageInvestorDetailsController}>
			<Flex direction="column" style={{ overflow: 'auto' }} pb="2rem" gap="1rem">
				<PageHeader customTitle={headerContent} hideBreadcrumbs>
					<Flex align="flex-end">
						<Button
							data-testid={
								TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_VIEW_MESSAGES_BUTTON
							}
							size="md"
							variant="default"
							onClick={() => navigateToMessages()}
							leftSection={<Icon variant="mail" size="lg" />}
						>
							{tMipManage('investors.view_messages')}
						</Button>
					</Flex>
				</PageHeader>
				{manageInvestorDetailsController?.getMIPInvestorDetailsDataRequest?.isFetching ? (
					<InlineLoader />
				) : null}
				<InvestorPersonalInformationSection />
				<DashCards />
				<BankDetailsSection />
				<PIVDetailsSection />
				<HoldingTable getSecurityTypeTranslation={getTypeTranslation} />
				<InvestmentTable
					getInvestmentReference={getInvestmentReference}
					getSecurityTypeTranslation={getTypeTranslation}
				/>
				<TransactionTable
					getInvestmentReference={getInvestmentReference}
					getSecurityTypeTranslation={getTypeTranslation}
				/>
				<InvestorDocumentWorkflowTable
					ref={manageInvestorDetailsController.contractTableRef}
					getInvestmentReference={getInvestmentReference}
				/>
				{checkIsTsaEmpty() && (
					<ManageInvestorSection
						navigateToTable={navigateToTable}
						investorId={manageInvestorDetailsController.investorId}
					/>
				)}
			</Flex>
		</ManageInvestorDetailsContext.Provider>
	);

	return (
		<PageAbsoluteContainer mb="1rem">
			<MIPManageInvestorsV2Context.Provider value={manageInvestorsControllerV2}>
				{childrenContent}
			</MIPManageInvestorsV2Context.Provider>
		</PageAbsoluteContainer>
	);
};
export const TableHeaderWrapper = styled.div`
	margin-top: ${rem(32)};
	margin-bottom: ${rem(16)};
`;
